import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { api, type Principal } from "./client";
import { useQueryClient } from "@tanstack/react-query";

type AuthStatus = "idle" | "loading" | "authenticated" | "unauthenticated" | "error";

export interface AuthState {
  status: AuthStatus;
  principal: Principal | null;
  accessToken: string | null;
  error: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, mfaCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [status, setStatus] = useState<AuthStatus>("idle");
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const principalScope = useRef('');
  const commitPrincipal = useCallback((next: Principal | null) => {
    const scope = next ? JSON.stringify([next.workspaceId, next.id, next.sessionId]) : '';
    if (principalScope.current !== scope) {
      // Cancel old-session continuations before mounting the next workspace.
      // This also scopes existing shared menus/settings/provider caches safely.
      queryClient.clear();
      principalScope.current = scope;
    }
    setPrincipal(next);
  }, [queryClient]);

  const restore = useCallback(async () => {
    setStatus("loading");
    setError(null);
    try {
      // Try to refresh the session first so the API client has a valid access
      // token. This avoids relying solely on the httpOnly access-token cookie
      // and prevents confusing 401s when the cookie is not present.
      const refreshed = await api.refresh();
      if (!refreshed) {
        throw new Error("No active session");
      }
      setAccessToken(api.getAccessToken());
      const me = await api.me();
      commitPrincipal(me);
      setStatus("authenticated");
    } catch (err) {
      api.setAccessToken(null);
      commitPrincipal(null);
      setStatus("unauthenticated");
      // swallow expected unauthorized errors on restore
      if (err && typeof err === "object" && "status" in err && (err as { status?: number }).status !== 401) {
        setError(err instanceof Error ? err.message : "Session restore failed");
      }
    }
  }, [commitPrincipal]);

  useEffect(() => {
    void restore();
  }, [restore]);

  const login = useCallback(async (email: string, password: string, mfaCode?: string) => {
    setStatus("loading");
    setError(null);
    try {
      const result = await api.login(email, password, true, mfaCode);
      api.setAccessToken(result.accessToken);
      setAccessToken(result.accessToken);
      commitPrincipal(result.user);
      setStatus("authenticated");
    } catch (err) {
      commitPrincipal(null);
      setStatus("error");
      if (err && typeof err === "object" && "problem" in err) {
        const problem = (err as { problem?: { detail?: string; title?: string } }).problem;
        setError(problem?.detail || problem?.title || "Login failed");
      } else {
        setError(err instanceof Error ? err.message : "Login failed");
      }
      throw err;
    }
  }, [commitPrincipal]);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      api.setAccessToken(null);
      setAccessToken(null);
      commitPrincipal(null);
      setStatus("unauthenticated");
      setError(null);
    }
  }, [commitPrincipal]);

  const refresh = useCallback(async () => {
    try {
      const refreshed = await api.refresh();
      if (!refreshed) {
        throw new Error("Session refresh failed");
      }
      setAccessToken(api.getAccessToken());
      const me = await api.me();
      commitPrincipal(me);
      setStatus("authenticated");
    } catch {
      api.setAccessToken(null);
      setAccessToken(null);
      commitPrincipal(null);
      setStatus("unauthenticated");
    }
  }, [commitPrincipal]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      principal,
      accessToken,
      error,
      isLoading: status === "loading" || status === "idle",
      isAuthenticated: status === "authenticated" && principal !== null,
      login,
      logout,
      refresh,
    }),
    [status, principal, accessToken, error, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function RequireAuth({ children }: { children: ReactNode }): ReactNode {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <AuthLoading />;
  if (!isAuthenticated) return <LoginRedirect />;
  return children;
}

function AuthLoading() {
  return (
    <div className="ev-auth-loading">
      <div className="ev-auth-loading__spinner" aria-label="Loading session" />
      <p>Restoring session…</p>
    </div>
  );
}

function LoginRedirect() {
  // Rendered outside router in this provider placement, so we rely on App.tsx to route
  return null;
}
